import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { ProfileResponse } from "../services/types";
import { getCurrentUser } from "../services/usersService";
import { tokenStorage } from "../services/api";
import { consumeSessionExpired } from "../utils/sessionExpiry";
import { clearScreenCache } from "../utils/screenCache";
import { loadMeWithRetry } from "../services/sessionPolicy";

interface AuthContextType {
  user: ProfileResponse | null;
  loading: boolean;
  setUser: (user: ProfileResponse | null) => void;
  refreshUser: () => Promise<void>;
  /**
   * Точечно обновить поля профиля в клиентском состоянии без запроса к серверу
   * (счётчики супер симпатий после отправки/покупки). Профиль не загружен — no-op.
   */
  patchUser: (patch: UserPatch) => void;
  isAuthenticated: boolean;
  /** При старте были токены, но сессию восстановить не удалось — лендинг покажет форму входа. */
  sessionExpired: boolean;
  /** Сбросить флаг после того, как сообщение об истёкшей сессии показано. */
  clearSessionExpired: () => void;
}

export type UserPatch = Partial<ProfileResponse> | ((user: ProfileResponse) => Partial<ProfileResponse>);

export function applyUserPatch(user: ProfileResponse | null, patch: UserPatch): ProfileResponse | null {
  if (!user) return user;
  return { ...user, ...(typeof patch === "function" ? patch(user) : patch) };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Токены могли быть отозваны ещё до перезагрузки (401 в apiFetch) — метка переживает reload.
  const [sessionExpired, setSessionExpired] = useState(consumeSessionExpired);

  /** trackExpiry — только для стартовой проверки: разлогин на старте показывает сообщение. */
  // Поколение загрузки: результат устаревшей (параллельной) загрузки не применяем.
  const loadGenRef = useRef(0);
  const loadUser = useCallback(async (trackExpiry = false) => {
    const generation = ++loadGenRef.current;
    try {
      // Access-токен мог истечь, но при живом refresh сессия восстановится внутри
      // apiFetch (401 → refresh → повтор), поэтому наличия refresh достаточно.
      const hasTokens = Boolean(tokenStorage.getAccessToken() || tokenStorage.getRefreshToken());

      if (!hasTokens) {
        setUser(null);
        setLoading(false);
        return;
      }

      const result = await loadMeWithRetry({
        fetchMe: getCurrentUser,
        tokensPresent: () => Boolean(tokenStorage.getAccessToken() || tokenStorage.getRefreshToken()),
        sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
        now: () => Date.now(),
        isStale: () => generation !== loadGenRef.current,
      });
      if (result.kind === "stale") return;
      if (result.kind === "ok") setUser(result.data);
      else if (result.kind === "expired") {
        setUser(null);
        if (trackExpiry) setSessionExpired(true);
      }
      // keep: сбой не доказывает отказ сессии — токены и прежний user не трогаем.
      setLoading(false);
    } catch (error) {
      console.error("Failed to refresh user:", error);
      // Исключение — не доказательство отказа сессии: токены и user не трогаем.
      if (generation === loadGenRef.current) setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(() => loadUser(), [loadUser]);
  const patchUser = useCallback((patch: UserPatch) => setUser((prev) => applyUserPatch(prev, patch)), []);

  useEffect(() => {
    void loadUser(true);
  }, [loadUser]);

  // Выход / смена аккаунта: последние ответы экранов (лента, чаты, уведомления)
  // принадлежали прежнему пользователю — см. utils/screenCache.ts.
  const userId = user?.id;
  useEffect(() => {
    if (userId == null) clearScreenCache();
  }, [userId]);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const isAuthenticated = !!user && !!tokenStorage.getAccessToken();

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        setUser,
        refreshUser,
        patchUser,
        isAuthenticated,
        sessionExpired,
        clearSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/** Storybook-only provider with static user (no API). */
export function MockAuthProvider({
  children,
  user,
}: {
  children: ReactNode;
  user?: ProfileResponse | null;
}) {
  const [currentUser, setUser] = useState<ProfileResponse | null>(user ?? null);
  return (
    <AuthContext.Provider
      value={{
        user: currentUser,
        loading: false,
        setUser,
        refreshUser: async () => {},
        patchUser: (patch) => setUser((prev) => applyUserPatch(prev, patch)),
        isAuthenticated: !!currentUser,
        sessionExpired: false,
        clearSessionExpired: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
